// TIP-611 — the order form's rules, with no React in them.
//
// The name guess, the credit-exposure format, the bucket the standfirst names,
// and the five confirmation variants all live here so each can be pinned by a
// test without rendering a modal.
import {
  CONFIRMATION_COPY,
  COUNTRY_LIST,
  EMIRATES,
  ORDER_COPY,
  ZONES,
} from "./orderFormFixtures";

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());

// TIP-611 — the subject's country governs the two fields above it.
//
// "Freezone or mainland" and "Emirate or state" are both UAE vocabularies:
// every named zone is a UAE authority and every emirate is a UAE emirate. Ask
// them of a company in India and the only truthful answers are the two the
// lists already carry for exactly this case.
//
// Resolved from the lists rather than written as literals, so a copy change to
// either option cannot leave this rule pointing at a value the dropdown has
// stopped offering.
export const UAE = COUNTRY_LIST.find((c) => c.code === "AE")?.name;
export const ZONE_NOT_KNOWN = ZONES.find((z) => z === "Not known");
export const OUTSIDE_UAE = EMIRATES.find((e) => e === "Outside the UAE");

export const isUAE = (country) => !!country && country === UAE;

/**
 * The zone and emirate a given subject country implies, or null where the
 * country implies nothing.
 *
 * A country not yet chosen implies nothing either: the subject's country is
 * deliberately not defaulted, and answering on its behalf before it is picked
 * would file the answer against a blank.
 */
export const impliedLocation = (country) => {
  if (!country || isUAE(country)) return null;
  return { zone: ZONE_NOT_KNOWN, emirate: OUTSIDE_UAE };
};

// The three fields the lock owns. `zoneOther` is in the set because it is the
// free text that "Other freezone or authority" opens: leaving a foreign
// registry name behind a locked "Not known" would strand it on screen.
export const LOCKED_LOCATION_FIELDS = ["zone", "zoneOther", "emirate"];

/** What a locked (outside-the-UAE) subject reads. */
export const lockedLocation = () => ({
  zone: ZONE_NOT_KNOWN,
  zoneOther: "",
  emirate: OUTSIDE_UAE,
});

const pick = (values, keys) =>
  keys.reduce((acc, key) => ({ ...acc, [key]: values?.[key] ?? "" }), {});

/**
 * What the freezone/emirate pair should become when the subject country
 * changes, and what to stash while it is locked.
 *
 * Outside the UAE the two fields are DISABLED, so the implied answers are
 * written unconditionally — a locked field the customer disagrees with is one
 * they cannot correct, so it must not show their old answer. Their answers are
 * stashed on the way in and restored on the way out, so a mis-click on the
 * country picker does not silently destroy work.
 *
 * The UAE, or a country not yet chosen, unlocks the pair and puts back whatever
 * was there before it locked.
 *
 * @param {string}  country the newly chosen subject country
 * @param {object}  current the form values as they stand
 * @param {?object} saved   answers stashed when the lock went on, or null
 * @returns {{values: object, saved: ?object}} values to merge, and the new stash
 */
export const locationForCountry = (country, current = {}, saved = null) => {
  const locking = !!impliedLocation(country);

  if (locking) {
    return {
      values: lockedLocation(),
      // Only stash on the way IN. Moving between two foreign countries must
      // not overwrite the stash with the locked values themselves.
      saved: saved || pick(current, LOCKED_LOCATION_FIELDS),
    };
  }

  // Unlocking. Restoring nothing when there is no stash leaves a UAE subject's
  // own answers exactly as they were.
  return { values: saved ? { ...saved } : {}, saved: null };
};

// Digits are dropped, and dots, underscores, hyphens and plus signs separate
// the parts. `hassan.ali@gulfmarine.ae` becomes `Hassan Ali`.
export const nameFromEmail = (email) => {
  const local = ((email || "").split("@")[0] || "").replace(/\d+/g, "");
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
};

// Non-digits are stripped and thousands separators are re-applied on every
// keystroke, so the field cannot hold a value the backend would reject.
export const formatExposure = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

// The header's `Your reference`, which is the contact's name and email once the
// email looks like one.
export const referenceSummary = (email, name) => {
  const trimmed = (email || "").trim();
  if (!isEmail(trimmed)) return ORDER_COPY.header.referenceEmpty;
  return name ? `${name}, ${trimmed}` : trimmed;
};

// A brief comes out of the bought bucket only when the plan cannot pay for it.
// One predicate, read by the standfirst, the footer and the confirmation, so
// the three cannot disagree about what is being spent.
export const spendsBoughtBrief = (allowance) => {
  if (!allowance) return false;
  const weekAtCap = allowance.week != null && allowance.week.remaining === 0;
  const monthAtCap = allowance.month.remaining === 0;
  return (weekAtCap || monthAtCap) && allowance.bought > 0;
};

export const resolveSourceNote = (allowance) => {
  const { sourceNote } = ORDER_COPY;
  if (spendsBoughtBrief(allowance)) {
    return allowance.bought === 1 ? sourceNote.boughtOne : sourceNote.boughtMany(allowance.bought);
  }
  return allowance.isMember
    ? sourceNote.month(allowance.month.remaining)
    : sourceNote.week(allowance.week.remaining);
};

export const resolveSpendNote = (allowance) => {
  const { spendNote } = ORDER_COPY.footer;
  if (spendsBoughtBrief(allowance)) return spendNote.bought;
  return allowance.isMember ? spendNote.member : spendNote.claimed;
};

// The six fields that carry a required marker. All six are enforced
// server-side too; this only stops the request leaving the browser.
export const REQUIRED_FIELDS = [
  "fullName",
  "company",
  "email",
  "mobile",
  "subjectName",
  "subjectCountry",
];

export const missingRequired = (values) =>
  REQUIRED_FIELDS.filter((key) => !String(values[key] ?? "").trim());

export const canSubmit = (values) => values.declaration && missingRequired(values).length === 0;

export const CONFIRMATION_VARIANTS = {
  CLAIMED_SOME_LEFT: "claimed-some-left",
  WEEK_NOW_ZERO: "week-now-zero",
  MONTH_NOW_ZERO: "month-now-zero",
  MEMBER_SOME_LEFT: "member-some-left",
  BOUGHT: "bought",
};

const V = CONFIRMATION_VARIANTS;

// The five wordings, resolved from the allowance as it stood when the order was
// placed. The frame never changes: only this sentence and the second button.
//
// Variant 5 is NEW. In the design the bought bucket is named on the order form
// and never on the confirmation, so a customer who has just spent a bought
// brief while the plan is exhausted falls through to variant 3 — which tells
// them the allowance resets on a future date and invites them to buy again.
// The bought branch is tested first for exactly that reason.
// TIP-655 — the reset dates come from the ALLOWANCE, so the confirmation
// sentence names the same day the card and the blocking banner do. They used
// to come from the RESET_DATES fixture, which is a hardcoded "1 September"
// whatever month it actually is.
//
// The labels are the backend's own (`reset.monthly_label` / `weekly_label`),
// so there is one source for every surface. A missing one drops the clause
// rather than printing "resets null": the customer is better told nothing
// about the date than told something wrong, which is the whole point here.
const resetClause = (label, phrasing) => (label ? ` ${phrasing(label)}` : "");

export const deriveConfirmation = (allowance) => {
  const { resetDate, weekResetDate } = allowance;
  const unitLabel = allowance.tier.unitLabel;

  if (spendsBoughtBrief(allowance)) {
    const extra = Math.max(allowance.bought - 1, 0);
    return {
      variant: V.BOUGHT,
      sentence: `That used 1 of your bought briefs. You have ${extra} left, and they do not expire.`,
      // At zero there is nothing left to spend, so the second button sells more
      // rather than reopening a form that cannot be paid for.
      secondary: extra > 0 ? CONFIRMATION_COPY.orderAnother : CONFIRMATION_COPY.buyMany,
      secondaryAction: extra > 0 ? "order" : "buy",
    };
  }

  const monthLeft = Math.max(allowance.month.remaining - 1, 0);

  if (monthLeft <= 0) {
    return {
      variant: V.MONTH_NOW_ZERO,
      sentence: `That was your last brief this month.${resetClause(resetDate, (d) => `The allowance resets ${d}.`)} You can buy a brief any time at ${unitLabel}.`,
      secondary: CONFIRMATION_COPY.buyOne,
      secondaryAction: "buy",
    };
  }

  // A Member has no weekly limit, so the week is never mentioned to them.
  if (allowance.isMember) {
    return {
      variant: V.MEMBER_SOME_LEFT,
      sentence: `One brief has come off your allowance. You have ${monthLeft} left this month.`,
      secondary: CONFIRMATION_COPY.orderAnother,
      secondaryAction: "order",
    };
  }

  const weekLeft = Math.max(allowance.week.remaining - 1, 0);

  if (weekLeft <= 0) {
    return {
      variant: V.WEEK_NOW_ZERO,
      sentence: weekResetDate
        ? `That was your last brief this week. ${monthLeft} are still in this month's allowance, opening again ${weekResetDate}.`
        : `That was your last brief this week. ${monthLeft} are still in this month's allowance.`,
      secondary: CONFIRMATION_COPY.buyOne,
      secondaryAction: "buy",
    };
  }

  return {
    variant: V.CLAIMED_SOME_LEFT,
    sentence: `One brief has come off your allowance. You have ${weekLeft} left this week, ${monthLeft} left this month.`,
    secondary: CONFIRMATION_COPY.orderAnother,
    secondaryAction: "order",
  };
};
